import { useContext } from "react";
import { GridList, GridListItem } from "react-aria-components";
import {
  Card,
  Collection,
  Content,
  Footer,
  Text,
  IllustratedMessage,
  Heading,
  ActionButton,
  ButtonGroup,
  Button,
  Link,
  DialogTrigger,
  LabeledValue,
} from "@react-spectrum/s2";
import NoElementsIcon from "@react-spectrum/s2/illustrations/linear/NoElements";
import NewIcon from "@react-spectrum/s2/icons/New";
import EditIcon from "@react-spectrum/s2/icons/Edit";
import AIGenerateIcon from "icon:../../../assets/AIGenerate.svg";

import { getQueryLoadingState } from "@app/utils";
import { SELECTION_TYPE } from "@app/hooks/videoWizard";
import { LinkChecker } from "@app/components/LinkChecker";
import { SyntaxHighlighter } from "@app/components/SyntaxHighlighter";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { NewCampaignDialog } from "../NewCampaignDialog";
import { PartnershipValuePreview } from "../PartnershipValuePreview";
import { CampaignPreview } from "../CampaignPreview";
import { useCampaignEditing } from "@app/hooks/useCampaignEditing";
import { Step } from "./Step";
import { CampaignPromptFooter } from "./CampaignPromptFooter";
import { DevToolbarItem } from "../DevToolbar";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const renderEmptyState = (message = "No campaigns found") => (
  <IllustratedMessage size="M" styles={style({ marginY: 64 })}>
    <Heading>{message}</Heading>
  </IllustratedMessage>
);

export const ScriptStep = ({
  onContinue,
  onBack,
  continueLabel,
  preview = "partnership-value",
  contentLabel = "script",
  canEditCampaign = true,
}) => {
  const {
    isStepPending,
    video,
    customerBranding,
    selectedCampaign,
    selectedPersona,
    template,
    updateVideo,
    editCampaign,
    editedCampaign,
    setEditedCampaign,
    setPromptHint,
    customerName,
    colorScheme,
  } = useContext(VideoWizardContext);

  const PreviewComponent =
    preview === "campaign" ? CampaignPreview : PartnershipValuePreview;

  const editing = useCampaignEditing({
    withScriptHistory: preview !== "campaign",
  });
  const { handleFieldChange } = editing;

  if (
    isStepPending ||
    (template.data?.campaignSelectionType === SELECTION_TYPE.NONE &&
      !selectedCampaign)
  ) {
    return (
      <Step.Loading
        title={`Preparing your ${contentLabel}`}
        subtitle="This may take a few moments, so please keep this tab open."
        ariaLabel="Generating script"
        icon={<NoElementsIcon />}
      />
    );
  }

  if (customerBranding.isError) {
    return (
      <Step.Error
        title="Failed to load script details"
        onRetry={() => customerBranding.refetch()}
      >
        There was an error generating your script. Please try again.
      </Step.Error>
    );
  }

  return (
    <>
      <Step
        styles={style({
          gridArea: "content",
          display: "flex",
          flexDirection: "column",
          gap: 32,
        })}
      >
        {!!video.data?.campaigns?.length &&
          template.data?.campaignSelectionType === SELECTION_TYPE.SINGLE && (
            <>
              <Step.Header
                title="Choose campaign focus"
                subtitle="You can adjust the details before moving to the next step."
              >
                <ButtonGroup styles={style({ marginTop: 24 })}>
                  <Button
                    variant="secondary"
                    fillStyle="outline"
                    onPress={() =>
                      setPromptHint("Add a campaign with focus on ")
                    }
                  >
                    <NewIcon />
                    <Text>Add new</Text>
                  </Button>
                  <Button
                    variant="secondary"
                    fillStyle="outline"
                    onPress={() => setPromptHint("Propose more campaign ideas")}
                  >
                    <AIGenerateIcon />
                    <Text>Generate more</Text>
                  </Button>
                </ButtonGroup>
              </Step.Header>

              <GridList
                loadingState={getQueryLoadingState(customerBranding)}
                selectionMode="single"
                selectionStyle="highlight"
                selectedKeys={video.data?.selectedCampaignIds}
                onSelectionChange={(keys) =>
                  updateVideo.mutate({
                    selectedCampaignIds: Array.from(keys),
                  })
                }
                renderEmptyState={() => renderEmptyState("No campaigns found")}
                aria-label="Select a campaign"
                layout="grid"
                className={style({
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(0, 400px))",
                  gridAutoRows: "minmax(0, max-content)",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 24,
                })}
              >
                <Collection
                  items={video.data?.campaigns}
                  dependencies={
                    process.env.NODE_ENV === "development" ? [+new Date()] : []
                  }
                >
                  {(item) => (
                    <GridListItem
                      key={item.id}
                      className={({ isSelected }) =>
                        style({
                          position: "relative",
                          width: "fit",
                          outlineWidth: 4,
                          outlineOffset: -2,
                          outlineStyle: "solid",
                          outlineColor: {
                            isSelected: {
                              colorScheme: {
                                light: "black/65",
                                dark: "white/65",
                              },
                            },
                            default: "transparent",
                          },
                          borderRadius: "lg",
                        })({ isSelected, colorScheme })
                      }
                    >
                      <Card
                        id={item?.id ?? item.index}
                        textValue={item.title}
                        variant="tertiary"
                        size="XL"
                        UNSAFE_style={{ outlineColor: "transparent" }}
                      >
                        <Content styles={style({ alignContent: "start" })}>
                          <Text styles={style({ font: "title" })}>
                            {item.title}
                          </Text>
                          <Text
                            styles={style({
                              font: "ui",
                              alignSelf: "start",
                            })}
                          >
                            {item.description}
                          </Text>
                          <ActionButton
                            aria-label="Edit campaign"
                            variant="primary"
                            isQuiet
                            onPress={() =>
                              setPromptHint(`Edit "${item.title}" campaign:\n`)
                            }
                            styles={style({
                              gridArea: "menu",
                              marginY: "calc(-1 * self(height))",
                              marginEnd: -8,
                            })}
                            data-slot="menu"
                          >
                            <EditIcon />
                          </ActionButton>
                        </Content>
                        <Footer>
                          <div
                            className={style({
                              display: "flex",
                              flexDirection: "column",
                              gap: 24,
                            })}
                          >
                            <LabeledValue
                              label="Tagline"
                              value={
                                <Text styles={style({ font: "title-xl" })}>
                                  {item.tagline}
                                </Text>
                              }
                            />
                            <LabeledValue
                              label="Landing page"
                              contextualHelp={
                                <LinkChecker url={item.landingPageURL} />
                              }
                              value={
                                <Link
                                  variant="secondary"
                                  href={item.landingPageURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  UNSAFE_className={style({
                                    wordBreak: "normal",
                                    overflowWrap: "anywhere",
                                    font: "ui-lg",
                                  })}
                                >
                                  {item.landingPageURL}
                                </Link>
                              }
                            />
                            <LabeledValue
                              label="Audiences"
                              value={
                                <div
                                  className={style({
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                  })}
                                >
                                  {item.audiences?.map((audience, idx) => (
                                    <Text
                                      key={idx}
                                      styles={style({ font: "ui" })}
                                    >
                                      {audience}
                                    </Text>
                                  ))}
                                </div>
                              }
                            />
                          </div>
                        </Footer>
                      </Card>
                    </GridListItem>
                  )}
                </Collection>
              </GridList>

              <DialogTrigger isOpen={!!editedCampaign}>
                <NewCampaignDialog
                  defaultCampaign={editedCampaign}
                  onSuccess={(campaign) => {
                    editCampaign.mutate(campaign);
                    setEditedCampaign(null);
                  }}
                  onClose={(close) => {
                    close();
                    setEditedCampaign(null);
                  }}
                />
              </DialogTrigger>
            </>
          )}

        {!!video.data?.campaigns?.length &&
          template.data?.campaignSelectionType === SELECTION_TYPE.NONE && (
            <>
              <Step.Header
                title={`Review your ${contentLabel}`}
                subtitle="You can adjust the details before moving to the next step."
              />

              {!!template.data?.campaignStructure?.length && (
                <PreviewComponent
                  selectedCampaign={selectedCampaign}
                  selectedPersona={selectedPersona}
                  campaignStructure={template.data?.campaignStructure}
                  templateData={template.data}
                  canEditCampaign={canEditCampaign}
                  onFieldChange={handleFieldChange}
                />
              )}

              {!template.data?.campaignStructure?.length && (
                <SyntaxHighlighter
                  code={JSON.stringify(selectedCampaign, null, 2)}
                />
              )}
            </>
          )}
      </Step>

      {(!!selectedCampaign || !!video.data?.campaigns?.length) &&
        !customerBranding.isError && (
          <CampaignPromptFooter
            editing={editing}
            placeholder={
              preview === "partnership-value"
                ? "Adjust script details"
                : "Adjust campaign details"
            }
          />
        )}

      <Step.Actions
        onBack={onBack}
        onContinue={onContinue}
        continueLabel={continueLabel}
        isContinueDisabled={
          template.data?.campaignSelectionType === SELECTION_TYPE.SINGLE
            ? !customerName || !video.data?.selectedCampaignIds?.length
            : !video.data?.campaigns?.length ||
              (customerBranding.isEnabled && customerBranding.isPending)
        }
      />

      <DevToolbarItem
        isPending={template.isPending}
        onPress={() => template.refetch()}
      >
        Refetch template
      </DevToolbarItem>
    </>
  );
};
